import {
  ArrowLeftRight,
  BarChart3,
  BookCopy,
  Handshake,
  Home,
  Megaphone,
  Settings,
  UploadCloud,
  Wallet,
} from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import InsightCard from '../components/InsightCard';
import OverviewDashboard, { type OverviewTopPageRow } from '../components/OverviewDashboard';
import SparklineCard from '../components/SparklineCard';
import TransferProcessBoard from '../components/TransferProcessBoard';

import { booksService, podBooksService } from '../../../shared/services';
import { useShop } from '../../../shared/store';
import { getProductMediaType } from '../../../shared/utils/productMedia';

type VendorSection =
  | 'overview'
  | 'books'
  | 'audio'
  | 'animations'
  | 'analytics'
  | 'revenue'
  | 'transfers'
  | 'announcements'
  | 'collaboration'
  | 'settings';
type ProductAudience = 'public' | 'kids' | 'adult';
type ProductKind = 'book' | 'audio' | 'animation' | 'pod';
type FeedbackStatus = 'idle' | 'pending' | 'success' | 'error';

interface FormFeedback {
  status: FeedbackStatus;
  message: string;
}

const sections: VendorSection[] = [
  'overview',
  'books',
  'audio',
  'animations',
  'analytics',
  'revenue',
  'transfers',
  'announcements',
  'collaboration',
  'settings',
];
const navItemsConfig: Array<Omit<DashboardNavItem, 'label'> & { labelKey?: string; label?: string }> = [
  { id: 'overview', labelKey: 'dashboardVendor.nav.overview', to: '/vendor/dashboard/overview', icon: Home },
  { id: 'books', labelKey: 'dashboardVendor.nav.books', to: '/vendor/dashboard/books', icon: BookCopy },
  { id: 'add-product', label: 'Add Product', to: '/vendor/add-product', icon: UploadCloud },
  { id: 'analytics', labelKey: 'dashboardVendor.nav.analytics', to: '/vendor/dashboard/analytics', icon: BarChart3 },
  { id: 'revenue', labelKey: 'dashboardVendor.nav.revenue', to: '/vendor/dashboard/revenue', icon: Wallet },
  { id: 'transfers', label: 'Transfers', to: '/vendor/dashboard/transfers', icon: ArrowLeftRight },
  { id: 'announcements', labelKey: 'dashboardVendor.nav.announcements', to: '/vendor/dashboard/announcements', icon: Megaphone },
  { id: 'collaboration', labelKey: 'dashboardVendor.nav.collaboration', to: '/vendor/dashboard/collaboration', icon: Handshake },
  { id: 'settings', labelKey: 'dashboardVendor.nav.settings', to: '/vendor/dashboard/settings', icon: Settings },
];

const sectionMetaKeys: Record<
  VendorSection,
  { titleKey?: string; descriptionKey?: string; fallbackTitle?: string; fallbackDescription?: string }
> = {
  overview: { titleKey: 'dashboardVendor.sections.overview.title', descriptionKey: 'dashboardVendor.sections.overview.description' },
  books: { titleKey: 'dashboardVendor.sections.books.title', descriptionKey: 'dashboardVendor.sections.books.description' },
  audio: {
    fallbackTitle: 'Audio Management',
    fallbackDescription: 'Manage your audio titles, pricing, and publishing status.',
  },
  animations: {
    fallbackTitle: 'Animation Management',
    fallbackDescription: 'Manage your animation titles, quality, and publishing status.',
  },
  analytics: {
    titleKey: 'dashboardVendor.sections.analytics.title',
    descriptionKey: 'dashboardVendor.sections.analytics.description',
  },
  revenue: { titleKey: 'dashboardVendor.sections.revenue.title', descriptionKey: 'dashboardVendor.sections.revenue.description' },
  transfers: {
    fallbackTitle: 'Transfer Process',
    fallbackDescription: 'Track incoming royalties, outgoing withdrawals, and transfer progress updates.',
  },
  announcements: {
    titleKey: 'dashboardVendor.sections.announcements.title',
    descriptionKey: 'dashboardVendor.sections.announcements.description',
  },
  collaboration: {
    titleKey: 'dashboardVendor.sections.collaboration.title',
    descriptionKey: 'dashboardVendor.sections.collaboration.description',
  },
  settings: { titleKey: 'dashboardVendor.sections.settings.title', descriptionKey: 'dashboardVendor.sections.settings.description' },
};

const initialFeedback: FormFeedback = { status: 'idle', message: '' };

const sectionKindMap: Partial<Record<VendorSection, ProductKind>> = {
  books: 'book',
  audio: 'audio',
  animations: 'animation',
};

const productKindSingularLabels: Record<ProductKind, string> = {
  book: 'Book',
  audio: 'Audio',
  animation: 'Animation',
  pod: 'POD',
};

const productKindEmptyState: Record<ProductKind, string> = {
  book: 'No books found in the database for this filter.',
  audio: 'No audio records found in the database for this filter.',
  animation: 'No animations found in the database for this filter.',
  pod: 'POD titles created here will appear on the POD pages after approval.',
};

const productKindFileAccept: Record<ProductKind, string> = {
  book: '.pdf,.epub,.mobi,.txt,application/pdf,application/epub+zip',
  audio: 'audio/*,.mp3,.wav,.ogg,.m4a,.aac',
  animation: 'video/*,.mp4,.webm,.ogg,.mov,.avi,.mkv',
  pod: '.pdf,application/pdf',
};

const getFeedbackTone = (status: FeedbackStatus): string => {
  if (status === 'success') {
    return 'text-emerald-700';
  }
  if (status === 'error') {
    return 'text-red-600';
  }
  if (status === 'pending') {
    return 'text-orange-600';
  }

  return 'text-slate-500';
};

const toActionErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error instanceof Error && error.message.trim()) {
    if (error.message.toLowerCase().includes('unauthorized')) {
      return 'Unauthorized. Please sign in again as a vendor to continue.';
    }

    return error.message;
  }

  return fallbackMessage;
};

const VendorDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ section?: string }>();
  const activeSection = sections.includes(params.section as VendorSection)
    ? (params.section as VendorSection)
    : 'overview';
  const { currentUser, orders, products, categories, refreshCatalog, isCatalogLoading } = useShop();
  const [productKind, setProductKind] = useState<ProductKind>(() => sectionKindMap[activeSection] ?? 'book');
  const [productTitleKey, setProductTitleKey] = useState('');
  const [productDescriptionKey, setProductDescriptionKey] = useState('');
  const [productCategoryKey, setProductCategoryKey] = useState('');
  const [productLanguage, setProductLanguage] = useState('en');
  const [productAudience, setProductAudience] = useState<ProductAudience>('public');
  const [productIsPODAvailable, setProductIsPODAvailable] = useState(true);
  const [productPrice, setProductPrice] = useState('');
  const [productCurrency, setProductCurrency] = useState('$');
  const [productMediaFile, setProductMediaFile] = useState<File | null>(null);
  const [productCoverFile, setProductCoverFile] = useState<File | null>(null);
  const [productThumbnailFile, setProductThumbnailFile] = useState<File | null>(null);
  const [trailerFile, setTrailerFile] = useState<File | null>(null);
  const [fullVideoFile, setFullVideoFile] = useState<File | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [podPageCount, setPodPageCount] = useState('');
  const [podSize, setPodSize] = useState('');
  const [podColorType, setPodColorType] = useState('');
  const [podCoverType, setPodCoverType] = useState('');
  const [podInteriorPdfFile, setPodInteriorPdfFile] = useState<File | null>(null);
  const [podCoverPdfFile, setPodCoverPdfFile] = useState<File | null>(null);
  const [podInteriorPdfUrl, setPodInteriorPdfUrl] = useState('');
  const [podCoverPdfUrl, setPodCoverPdfUrl] = useState('');
  const [productFeedback, setProductFeedback] = useState<FormFeedback>(initialFeedback);
  const [animationTitleKey, setAnimationTitleKey] = useState('');
  const [animationDescriptionKey, setAnimationDescriptionKey] = useState('');
  const [animationVideoFile, setAnimationVideoFile] = useState<File | null>(null);
  const [animationThumbnailFile, setAnimationThumbnailFile] = useState<File | null>(null);
  const [animationCategoryKey, setAnimationCategoryKey] = useState('');
  const [animationLanguage, setAnimationLanguage] = useState('en');
  const [animationAudience, setAnimationAudience] = useState<'kids' | 'adult' | 'adults' | 'mixed' | 'public'>('kids');
  const [animationFeedback, setAnimationFeedback] = useState<FormFeedback>(initialFeedback);
  const [audioTitleKey, setAudioTitleKey] = useState('');
  const [audioDescriptionKey, setAudioDescriptionKey] = useState('');
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioThumbnailFile, setAudioThumbnailFile] = useState<File | null>(null);
  const [audioCategoryKey, setAudioCategoryKey] = useState('');
  const [audioLanguage, setAudioLanguage] = useState('en');
  const [audioAudience, setAudioAudience] = useState<'kids' | 'adult' | 'adults' | 'mixed' | 'public'>('kids');
  const [audioFeedback, setAudioFeedback] = useState<FormFeedback>(initialFeedback);
  const [showProductModal, setShowProductModal] = useState(false);
  const isCatalogManagementSection = activeSection === 'books' || activeSection === 'audio' || activeSection === 'animations';
  const resolveTranslation = (key: string | undefined, fallback: string): string => {
    if (!key) {
      return fallback;
    }
    const translated = t(key);
    return translated === key ? fallback : translated;
  };
  const navItems = useMemo<DashboardNavItem[]>(
    () =>
      navItemsConfig.map((item) => ({
        ...item,
        label: item.label ?? resolveTranslation(item.labelKey, item.id),
      })),
    [t],
  );
  const sectionMeta = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(sectionMetaKeys).map(([sectionKey, meta]) => [
          sectionKey,
          {
            title: resolveTranslation(meta.titleKey, meta.fallbackTitle ?? sectionKey),
            description: resolveTranslation(meta.descriptionKey, meta.fallbackDescription ?? ''),
          },
        ]),
      ) as Record<VendorSection, { title: string; description: string }>,
    [t],
  );

  const metrics = useMemo(() => {
    const grossSales = orders.reduce((sum, order) => sum + order.total, 0);
    const royalties = grossSales * 0.35;
    const pendingPayout = royalties * 0.42;

    return {
      grossSales,
      royalties,
      pendingPayout,
    };
  }, [orders]);
  const formatCompactNumber = (value: number): string => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value);
  const formatDecimalCompact = (value: number): string =>
    new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  const vendorPerformance = useMemo(() => {
    const revenueByMonth = Array.from({ length: 12 }, () => 0);
    const ordersByMonth = Array.from({ length: 12 }, () => 0);

    orders.forEach((order) => {
      const parsedDate = new Date(order.createdAt);
      if (Number.isNaN(parsedDate.getTime())) {
        return;
      }

      const monthIndex = parsedDate.getMonth();
      revenueByMonth[monthIndex] += order.total;
      ordersByMonth[monthIndex] += 1;
    });

    const normalizedLine = ordersByMonth.map((value) => (value === 0 ? 1 : value));
    return { bars: revenueByMonth, line: normalizedLine };
  }, [orders]);
  const deliveredOrders = useMemo(() => orders.filter((order) => order.status === 'Arrived').length, [orders]);
  const vendorConversionRate = useMemo(() => {
    if (orders.length === 0) {
      return 65.2;
    }

    return (deliveredOrders / orders.length) * 100;
  }, [deliveredOrders, orders.length]);
  const vendorTopPages = useMemo<OverviewTopPageRow[]>(
    () => [
      { path: 'vendor/overview', pageViews: 220 + products.length * 8, exitRate: 4.7 },
      { path: 'vendor/books', pageViews: 210 + products.length * 5, exitRate: 8.5 },
      { path: 'vendor/audio', pageViews: 172 + Math.round(metrics.royalties / 25), exitRate: 11.9 },
      { path: 'vendor/animations', pageViews: 160 + Math.round(metrics.royalties / 28), exitRate: 9.4 },
      { path: 'vendor/categories', pageViews: 145 + categories.length * 6, exitRate: 7.8 },
      { path: 'vendor/analytics', pageViews: 150 + Math.round(metrics.grossSales / 65), exitRate: 6.4 },
      { path: 'vendor/print-on-demand', pageViews: 133 + Math.round(metrics.pendingPayout / 22), exitRate: 13.6 },
    ],
    [categories.length, metrics.grossSales, metrics.pendingPayout, metrics.royalties, products.length],
  );
  const categoryNameById = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  );
  const categoryKeyOptions = useMemo(() => {
    const seenValues = new Set<string>();
    return categories
      .map((category) => {
        const value = category.slug?.trim() || category.name.trim() || category.id;
        if (!value || seenValues.has(value)) {
          return null;
        }
        seenValues.add(value);
        return { value, label: category.name };
      })
      .filter((categoryOption): categoryOption is { value: string; label: string } => Boolean(categoryOption));
  }, [categories]);
  const resolvedProductCategoryKey = productCategoryKey || categoryKeyOptions[0]?.value || '';
  const managedProductsByKind = useMemo(() => {
    const grouped: Record<ProductKind, typeof products> = {
      book: [],
      audio: [],
      animation: [],
      pod: [],
    };
    products.forEach((product) => {
      grouped[getProductMediaType(product)].push(product);
    });
    return grouped;
  }, [products]);
  const managedProducts = managedProductsByKind[productKind];
  const managedAudio = managedProductsByKind.audio;
  const managedAnimations = managedProductsByKind.animation;
  const selectedProductKindEmptyState = productKindEmptyState[productKind];
  const isProductSubmitting = productFeedback.status === 'pending';
  const isAnimationSubmitting = animationFeedback.status === 'pending';
  const isAudioSubmitting = audioFeedback.status === 'pending';
  const handleProductKindChange = (nextKind: ProductKind) => {
    setProductKind(nextKind);
    setProductFeedback(initialFeedback);
    setProductMediaFile(null);
    setProductCoverFile(null);
    setProductThumbnailFile(null);
    setTrailerFile(null);
    setFullVideoFile(null);
    setPictureFile(null);
    setPodPageCount('');
    setPodSize('');
    setPodColorType('');
    setPodCoverType('');
    setPodInteriorPdfFile(null);
    setPodCoverPdfFile(null);
    setPodInteriorPdfUrl('');
    setPodCoverPdfUrl('');
  };

  const handleCreateProduct = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedTitleKey = productTitleKey.trim();
    if (!trimmedTitleKey) {
      setProductFeedback({
        status: 'error',
        message: 'titleKey is required.',
      });
      return;
    }

    if (productKind === 'pod') {
      const parsedPageCount = Number(podPageCount);

      if (!Number.isFinite(parsedPageCount) || parsedPageCount <= 0) {
        setProductFeedback({
          status: 'error',
          message: 'pageCount is required and must be greater than 0.',
        });
        return;
      }

      setProductFeedback({
        status: 'pending',
        message: 'Creating POD book...',
      });

      try {
        const createdPodBook = await podBooksService.createPodBook({
          name: trimmedTitleKey,
          pageCount: parsedPageCount,
          size: podSize,
          colorType: podColorType,
          coverType: podCoverType,
          interiorPdf: podInteriorPdfFile,
          coverPdf: podCoverPdfFile,
          interiorPdfUrl: podInteriorPdfUrl,
          coverPdfUrl: podCoverPdfUrl,
        });

        await refreshCatalog();
        
        // Notification and Redirect
        setProductFeedback({
          status: 'success',
          message: `POD book "${createdPodBook.title}" created successfully! Redirecting...`,
        });

        setTimeout(() => {
          navigate(`/products/${createdPodBook.id}`);
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }, 1500);
        return;
      } catch (error: unknown) {
        setProductFeedback({
          status: 'error',
          message: toActionErrorMessage(error, 'Unable to create POD book.'),
        });
        return;
      }
    }

    const trimmedDescriptionKey = productDescriptionKey.trim();
    if (!trimmedDescriptionKey) {
      setProductFeedback({
        status: 'error',
        message: 'descriptionKey is required.',
      });
      return;
    }

    const trimmedLanguage = productLanguage.trim();
    if (!trimmedLanguage) {
      setProductFeedback({
        status: 'error',
        message: 'language is required.',
      });
      return;
    }

    if (productKind !== 'animation' && !productMediaFile) {
      setProductFeedback({
        status: 'error',
        message: `${productKind === 'book' ? 'bookFile' : 'audioFile'} is required.`,
      });
      return;
    }
    if (productKind === 'animation' && (!trailerFile || !fullVideoFile || !pictureFile)) {
      setProductFeedback({
        status: 'error',
        message: 'trailerFile, fullVideoFile, and pictureFile are required for animation.',
      });
      return;
    }

    const trimmedCategoryKey = resolvedProductCategoryKey.trim();
    let parsedPrice: number | undefined;

    if (!trimmedCategoryKey) {
      setProductFeedback({
        status: 'error',
        message: 'categoryKey is required.',
      });
      return;
    }

    const parsed = Number(productPrice);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setProductFeedback({
        status: 'error',
        message: 'price is required and must be >= 0.',
      });
      return;
    }
    parsedPrice = Number(parsed.toFixed(2));

    const trimmedCurrency = productCurrency.trim().toUpperCase();
    if (!trimmedCurrency) {
      setProductFeedback({
        status: 'error',
        message: 'currency is required.',
      });
      return;
    }

    setProductFeedback({
      status: 'pending',
      message: 'Uploading item...',
    });

    try {
      const createdProduct = await booksService.createBook({
        titleKey: trimmedTitleKey,
        descriptionKey: trimmedDescriptionKey,
        price: parsedPrice,
        categoryKey: trimmedCategoryKey,
        language: trimmedLanguage,
        audience: productAudience,
        isPOD: productIsPODAvailable,
        bookFile: productKind === 'book' ? productMediaFile : null,
        audioFile: productKind === 'audio' ? productMediaFile : null,
        videoFile: fullVideoFile,
        trailerFile: trailerFile,
        coverFile: productCoverFile || pictureFile,
      });

      await refreshCatalog();
      
      setProductFeedback({
        status: 'success',
        message: `${productKindSingularLabels[productKind]} "${trimmedTitleKey}" added successfully! Redirecting...`,
      });

      // Redirect to the new product page so they can view it
      setTimeout(() => {
        const productId = createdProduct?.id || '';
        if (productId) {
          navigate(`/products/${productId}`);
          // Then eventually redirect to home page as requested
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          navigate('/');
        }
      }, 1500);

    } catch (error: unknown) {
      setProductFeedback({
        status: 'error',
        message: toActionErrorMessage(error, 'Unable to add product.'),
      });
    }
  };
  const handleUploadAnimation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void animationVideoFile;
    void animationThumbnailFile;
    setAnimationFeedback({
      status: 'error',
      message: 'Animation upload moved to the unified catalog form above.',
    });
  };
  const handleUploadAudio = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void audioFile;
    void audioThumbnailFile;
    setAudioFeedback({
      status: 'error',
      message: 'Audio upload moved to the unified catalog form above.',
    });
  };
  const activePageMeta = isCatalogManagementSection
    ? {
        title: 'Catalog Management',
        description: 'Select books, audio, or animations and view matching records from the database.',
      }
    : sectionMeta[activeSection];

  return (
    <>
      <DashboardFrame
      brand={t('dashboardVendor.brand')}
      roleLabel={t('dashboardVendor.roleLabel')}
      userName={currentUser?.name ?? t('dashboardVendor.defaultUserName')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle={activePageMeta.title}
      pageDescription={activePageMeta.description}
      navItems={navItems}
      activeNavId={activeSection}
      headerAction={
        isCatalogManagementSection ? (
          <button
            type="button"
            onClick={() => setShowProductModal(true)}
            className="rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
          >
            + Add Product
          </button>
        ) : (
          <Link
            to="/vendor/dashboard/overview"
            className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {t('dashboardVendor.actions.browseBooks')}
          </Link>
        )
      }
      rightPanel={
        activeSection === 'overview' || activeSection === 'transfers' ? undefined : (
          <div className="space-y-4">
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Upcoming Payout</h3>
              <p className="mt-2 text-2xl font-semibold text-orange-600">$ {metrics.pendingPayout.toFixed(2)}</p>
              <p className="mt-1 text-xs text-slate-600">Expected Friday payout cycle.</p>
            </article>
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Quick Alerts</h3>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                <li className="rounded-lg bg-orange-50 p-2">Audio quality review pending for one upload.</li>
                <li className="rounded-lg bg-orange-50 p-2">New collaboration request received.</li>
                <li className="rounded-lg bg-orange-50 p-2">Print-on-demand checklist is 60% complete.</li>
              </ul>
            </article>
          </div>
        )
      }
    >
      {activeSection === 'overview' && (
        <OverviewDashboard
          greeting="WELCOME!"
          alertMessage="Publishing queue is active. Keep optimizing metadata to increase discoverability."
          statCards={[
            {
              id: 'published-books',
              title: 'Published Books',
              value: formatCompactNumber(products.length),
              trendValue: '+2.3%',
              trendLabel: 'Last Week',
              isPositive: true,
              icon: BookCopy,
            },
            {
              id: 'gross-sales',
              title: 'Gross Sales',
              value: `$ ${formatDecimalCompact(metrics.grossSales)}`,
              trendValue: '+8.1%',
              trendLabel: 'Last Month',
              isPositive: true,
              icon: Wallet,
            },
            {
              id: 'royalties',
              title: 'Royalties',
              value: `$ ${formatDecimalCompact(metrics.royalties)}`,
              trendValue: '-0.3%',
              trendLabel: 'Last Month',
              isPositive: false,
              icon: BarChart3,
            },
            {
              id: 'pending-payout',
              title: 'Pending Payout',
              value: `$ ${formatDecimalCompact(metrics.pendingPayout)}`,
              trendValue: '-10.6%',
              trendLabel: 'Last Month',
              isPositive: false,
              icon: UploadCloud,
            },
          ]}
          performanceBars={vendorPerformance.bars}
          performanceLine={vendorPerformance.line}
          conversionRate={vendorConversionRate}
          thisWeekValue={formatDecimalCompact(metrics.royalties * 0.4)}
          lastWeekValue={formatDecimalCompact(metrics.royalties * 0.52)}
          topPages={vendorTopPages}
          recentOrders={orders}
          action={
            <Link
              to="/vendor/print-on-demand"
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
            >
              + Create Order
            </Link>
          }
        />
      )}

      {isCatalogManagementSection && (
        <div className="space-y-4 pb-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">My Products</h3>
                <p className="mt-1 text-sm text-slate-500">Products you have uploaded to the catalog.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowProductModal(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
              >
                + Add Product
              </button>
            </div>
          </article>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {managedProducts.length === 0 ? (
              <article className="col-span-full rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-500">
                {selectedProductKindEmptyState}
              </article>
            ) : (
              managedProducts.map((product) => (
                <article key={product.id} className="overflow-hidden rounded-2xl border border-orange-100 bg-white">
                  <img src={product.imageUrl} alt={t(product.nameKey)} className="h-32 w-full object-cover" />
                  <div className="space-y-2 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">{t(product.nameKey)}</h3>
                      <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                        {productKindSingularLabels[getProductMediaType(product)]}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {product.categoryId
                        ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
                        : t(product.categoryKey)}
                    </p>
                    <div className="flex gap-2">
                      <button type="button" className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">Edit</button>
                      <button type="button" className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700">Delete</button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      )}

      {activeSection === 'audio' && !isCatalogManagementSection && (
        <div className="space-y-4 pb-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Add Audio</h3>
            <p className="mt-1 text-sm text-slate-500">
              Upload audio using multipart fields: titleKey, descriptionKey, audioFile, thumbnailFile, categoryKey, language, audience.
            </p>
            <form onSubmit={handleUploadAudio} className="mt-4 grid gap-3 text-sm">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">titleKey</span>
                  <input
                    value={audioTitleKey}
                    onChange={(event) => setAudioTitleKey(event.target.value)}
                    placeholder="audio.newStory.title"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">descriptionKey</span>
                  <input
                    value={audioDescriptionKey}
                    onChange={(event) => setAudioDescriptionKey(event.target.value)}
                    placeholder="audio.newStory.desc"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">audioFile *</span>
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(event) => setAudioFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">thumbnailFile</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => setAudioThumbnailFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="grid gap-1">
                  <span className="text-slate-600">Choose Category</span>
                  <select
                    value={audioCategoryKey}
                    onChange={(event) => setAudioCategoryKey(event.target.value)}
                    className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                  >
                    <option value="">No category (optional)</option>
                    {categoryKeyOptions.map((categoryOption) => (
                      <option key={categoryOption.value} value={categoryOption.value}>
                        {categoryOption.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">language</span>
                  <input
                    value={audioLanguage}
                    onChange={(event) => setAudioLanguage(event.target.value)}
                    placeholder="en"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">audience</span>
                  <select
                    value={audioAudience}
                    onChange={(event) => setAudioAudience(event.target.value as 'kids' | 'adult' | 'adults' | 'mixed' | 'public')}
                    className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                  >
                    <option value="kids">kids</option>
                    <option value="public">public</option>
                    <option value="adult">adult</option>
                    <option value="adults">adults</option>
                    <option value="mixed">mixed</option>
                  </select>
                </label>
              </div>
              <button
                type="submit"
                disabled={isAudioSubmitting || isCatalogLoading}
                className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isAudioSubmitting ? 'Uploading...' : 'Upload Audio'}
              </button>
              {audioFeedback.status !== 'idle' && (
                <p className={`text-xs font-medium ${getFeedbackTone(audioFeedback.status)}`}>
                  {audioFeedback.message}
                </p>
              )}
            </form>
          </article>

          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-slate-900">Audio Catalog</h3>
              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                {managedAudio.length} title{managedAudio.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-600">Manage audio stories, metadata, and publication readiness.</p>
          </article>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {managedAudio.length === 0 ? (
              <article className="col-span-full rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-500">
                No audio products found. Upload audio using the form above.
              </article>
            ) : (
              managedAudio.map((product) => (
                <article key={product.id} className="overflow-hidden rounded-2xl border border-orange-100 bg-white">
                  <img src={product.imageUrl} alt={t(product.nameKey)} className="h-32 w-full object-cover" />
                  <div className="space-y-2 p-4">
                    <h3 className="text-sm font-semibold text-slate-900">{t(product.nameKey)}</h3>
                    <p className="text-xs text-slate-500">
                      {product.categoryId
                        ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
                        : t(product.categoryKey)}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      <span className="rounded-full bg-orange-50 px-2 py-1">{product.reviewCount} reviews</span>
                      <span className="rounded-full bg-orange-50 px-2 py-1">Stock {product.stock}</span>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">Edit</button>
                      <button type="button" className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700">Archive</button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      )}

      {activeSection === 'animations' && !isCatalogManagementSection && (
        <div className="space-y-4 pb-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Add Animation</h3>
            <p className="mt-1 text-sm text-slate-500">
              Upload animation using multipart fields: titleKey, descriptionKey, videoFile, thumbnailFile, categoryKey, language, audience.
            </p>
            <form onSubmit={handleUploadAnimation} className="mt-4 grid gap-3 text-sm">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">titleKey</span>
                  <input
                    value={animationTitleKey}
                    onChange={(event) => setAnimationTitleKey(event.target.value)}
                    placeholder="animation.abc.title"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">descriptionKey</span>
                  <input
                    value={animationDescriptionKey}
                    onChange={(event) => setAnimationDescriptionKey(event.target.value)}
                    placeholder="animation.abc.desc"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">videoFile *</span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(event) => setAnimationVideoFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">thumbnailFile</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => setAnimationThumbnailFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="grid gap-1">
                  <span className="text-slate-600">Choose Category</span>
                  <select
                    value={animationCategoryKey}
                    onChange={(event) => setAnimationCategoryKey(event.target.value)}
                    className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                  >
                    <option value="">No category (optional)</option>
                    {categoryKeyOptions.map((categoryOption) => (
                      <option key={categoryOption.value} value={categoryOption.value}>
                        {categoryOption.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">language</span>
                  <input
                    value={animationLanguage}
                    onChange={(event) => setAnimationLanguage(event.target.value)}
                    placeholder="en"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">audience</span>
                  <select
                    value={animationAudience}
                    onChange={(event) => setAnimationAudience(event.target.value as 'kids' | 'adult' | 'adults' | 'mixed' | 'public')}
                    className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                  >
                    <option value="kids">kids</option>
                    <option value="public">public</option>
                    <option value="adult">adult</option>
                    <option value="adults">adults</option>
                    <option value="mixed">mixed</option>
                  </select>
                </label>
              </div>
              <button
                type="submit"
                disabled={isAnimationSubmitting || isCatalogLoading}
                className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isAnimationSubmitting ? 'Uploading...' : 'Upload Animation'}
              </button>
              {animationFeedback.status !== 'idle' && (
                <p className={`text-xs font-medium ${getFeedbackTone(animationFeedback.status)}`}>
                  {animationFeedback.message}
                </p>
              )}
            </form>
          </article>

          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-slate-900">Animation Catalog</h3>
              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                {managedAnimations.length} title{managedAnimations.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-600">Manage animation stories, metadata, and publication readiness.</p>
          </article>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {managedAnimations.length === 0 ? (
              <article className="col-span-full rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-500">
                No animation products found. Upload animation using the form above.
              </article>
            ) : (
              managedAnimations.map((product) => (
                <article key={product.id} className="overflow-hidden rounded-2xl border border-orange-100 bg-white">
                  <img src={product.imageUrl} alt={t(product.nameKey)} className="h-32 w-full object-cover" />
                  <div className="space-y-2 p-4">
                    <h3 className="text-sm font-semibold text-slate-900">{t(product.nameKey)}</h3>
                    <p className="text-xs text-slate-500">
                      {product.categoryId
                        ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
                        : t(product.categoryKey)}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      <span className="rounded-full bg-orange-50 px-2 py-1">{product.reviewCount} reviews</span>
                      <span className="rounded-full bg-orange-50 px-2 py-1">Stock {product.stock}</span>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">Edit</button>
                      <button type="button" className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700">Archive</button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      )}

      {activeSection === 'analytics' && (
        <div className="space-y-4 pb-2">
          <div className="grid gap-4 xl:grid-cols-2">
            <SparklineCard title="Reader Progress" subtitle="Completion per week" values={[51, 53, 57, 55, 60, 63, 62]} />
            <SparklineCard title="Buyer Growth" subtitle="New buyers per week" values={[8, 9, 12, 11, 14, 17, 18]} />
          </div>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Buyer Demographics</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              <p className="rounded-lg bg-orange-50 p-2 text-sm text-slate-700">East Africa: 58%</p>
              <p className="rounded-lg bg-orange-50 p-2 text-sm text-slate-700">West Africa: 18%</p>
              <p className="rounded-lg bg-orange-50 p-2 text-sm text-slate-700">Europe: 14%</p>
              <p className="rounded-lg bg-orange-50 p-2 text-sm text-slate-700">North America: 10%</p>
            </div>
          </article>
        </div>
      )}

      {activeSection === 'revenue' && (
        <div className="grid gap-4 pb-2 lg:grid-cols-3">
          <InsightCard title="Gross Sales" value={`$ ${metrics.grossSales.toFixed(2)}`} subtitle="All-time linked sales" />
          <InsightCard title="Royalties" value={`$ ${metrics.royalties.toFixed(2)}`} subtitle="Current estimated royalties" />
          <InsightCard title="Pending Payout" value={`$ ${metrics.pendingPayout.toFixed(2)}`} subtitle="Next payout estimate" />
        </div>
      )}

      {activeSection === 'transfers' && <TransferProcessBoard role="vendor" />}

      {activeSection === 'announcements' && (
        <div className="space-y-3 pb-2">
          {[
            'Platform update: payout reports include per-book breakdown.',
            'New multilingual metadata fields are now available.',
            'Print-on-demand beta now accepts paperback templates.',
          ].map((announcement) => (
            <article key={announcement} className="rounded-2xl border border-orange-100 bg-white p-4 text-sm text-slate-700">
              {announcement}
            </article>
          ))}
        </div>
      )}

      {activeSection === 'collaboration' && (
        <div className="grid gap-4 pb-2 md:grid-cols-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Co-author Invites</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="rounded-lg bg-orange-50 p-2">Invite pending: Aline Mukamana</li>
              <li className="rounded-lg bg-orange-50 p-2">Invite accepted: John Otieno</li>
            </ul>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Bundle Opportunities</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="rounded-lg bg-orange-50 p-2">African Sci-Fi Starter Pack</li>
              <li className="rounded-lg bg-orange-50 p-2">Children Story Collection</li>
            </ul>
          </article>
        </div>
      )}

      {activeSection === 'settings' && (
        <div className="grid gap-4 pb-2 md:grid-cols-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Profile</h3>
            <div className="mt-3 grid gap-3 text-sm">
              <input defaultValue={currentUser?.name ?? ''} className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none" />
              <input defaultValue={currentUser?.email ?? ''} className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none" />
              <button type="button" className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white">Save</button>
            </div>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Payout Preferences</h3>
            <div className="mt-3 grid gap-3 text-sm">
              <input placeholder="Bank name" className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none" />
              <input placeholder="Account number" className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none" />
              <button type="button" className="rounded-xl border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700">Update payout info</button>
            </div>
          </article>
        </div>
      )}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <article className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-orange-100 bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Create Product</h3>
              <button
                type="button"
                onClick={() => setShowProductModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateProduct} className="mt-4 grid gap-3 text-sm">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">Kind</span>
                  <select
                    value={productKind}
                    onChange={(event) => setProductKind(event.target.value as ProductKind)}
                    className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                  >
                    <option value="book">Book</option>
                    <option value="audio">Audio</option>
                    <option value="animation">Animation</option>
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">titleKey *</span>
                  <input
                    value={productTitleKey}
                    onChange={(event) => setProductTitleKey(event.target.value)}
                    placeholder="product.title.key"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              <label className="grid gap-1">
                <span className="text-slate-600">descriptionKey *</span>
                <input
                  value={productDescriptionKey}
                  onChange={(event) => setProductDescriptionKey(event.target.value)}
                  placeholder="product.desc.key"
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">Category *</span>
                  <select
                    value={resolvedProductCategoryKey}
                    onChange={(event) => setProductCategoryKey(event.target.value)}
                    className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                  >
                    {categoryKeyOptions.map((categoryOption) => (
                      <option key={categoryOption.value} value={categoryOption.value}>
                        {categoryOption.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">Language</span>
                  <input
                    value={productLanguage || 'en'}
                    onChange={(event) => setProductLanguage(event.target.value)}
                    placeholder="en"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">Price *</span>
                  <input
                    type="number"
                    value={productPrice}
                    onChange={(event) => setProductPrice(event.target.value)}
                    placeholder="0.00"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">Currency</span>
                  <input
                    value={productCurrency}
                    onChange={(event) => setProductCurrency(event.target.value)}
                    placeholder="$"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              {productKind === 'book' && (
                <label className="grid gap-1">
                  <span className="text-slate-600">bookFile *</span>
                  <input
                    type="file"
                    accept={productKindFileAccept.book}
                    onChange={(event) => setProductMediaFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              )}
              {productKind === 'audio' && (
                <label className="grid gap-1">
                  <span className="text-slate-600">audioFile *</span>
                  <input
                    type="file"
                    accept={productKindFileAccept.audio}
                    onChange={(event) => setProductMediaFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              )}
              {productKind === 'animation' && (
                <>
                  <label className="grid gap-1">
                    <span className="text-slate-600">trailerFile *</span>
                    <input
                      type="file"
                      accept={productKindFileAccept.animation}
                      onChange={(event) => setTrailerFile(event.target.files?.[0] ?? null)}
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-slate-600">fullVideoFile *</span>
                    <input
                      type="file"
                      accept={productKindFileAccept.animation}
                      onChange={(event) => setFullVideoFile(event.target.files?.[0] ?? null)}
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-slate-600">pictureFile *</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => setPictureFile(event.target.files?.[0] ?? null)}
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                  </label>
                </>
              )}
              {(productKind === 'book' || productKind === 'audio') && (
                <label className="grid gap-1">
                  <span className="text-slate-600">coverFile</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => setProductCoverFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isProductSubmitting}
                  className="flex-1 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isProductSubmitting ? 'Uploading...' : 'Add Product'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                >
                  Cancel
                </button>
              </div>
              {productFeedback.status !== 'idle' && (
                <p className={`text-xs font-medium ${getFeedbackTone(productFeedback.status)}`}>
                  {productFeedback.message}
                </p>
              )}
            </form>
          </article>
        </div>
      )}
    </DashboardFrame>
    </>
  );
};

export default VendorDashboardPage;
